import { type FC, useCallback, useMemo, useState } from "react";
import styles from "./copilotReport.module.scss";
import { connect, type ConnectedProps } from "react-redux";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { ChartWrapper, GroupedBarChart, HorizontalBarChart, LineChart, PieChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { type RootState } from "features/Application/globaltypes/redux";
import {
  useCopilotUsersTrend,
  useCopilotAppEngagementTrend,
  useCopilotUserEngagement,
  useCopilotAppEngagementTimeseries,
} from "./queries";
import type { FlattenedCopilotUsageRecord } from "./types";
import { COPILOT_APP_COLOR, COPILOT_APP_DISPLAY } from "./constants";
import { reportingColors } from "features/Library/Common/utils/constants";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import {
  createDateRange,
  defaultDateFilter,
  type FormattedLineData,
  getFormattedTimeStringFromPeriod,
  lineChartMargins,
  noData,
  validBarData,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "components/common/tooltip";
import { selectMenuItemsData } from "features/Reporting/state/menu/integrationMenuSlice";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import sharedStyles from "../graphReport.module.scss";

const TOOLTIP_CONTENT = "Data shown here is only from Microsoft 365 Copilot, and not Copilot Chat.";

const ACTIVE_USERS_TITLES = ["Active Users - Column Chart", "Active Users - Line Chart"];

const USAGE_BY_APPLICATION_TITLES = ["Usage by Application - Bar Chart", "Usage by Application - Line Chart"];

const USAGE_BY_APPLICATION_LEGEND = [
  COPILOT_APP_DISPLAY.copilot_msft_teams,
  COPILOT_APP_DISPLAY.copilot_word,
  COPILOT_APP_DISPLAY.copilot_excel,
  COPILOT_APP_DISPLAY.copilot_power_point,
  COPILOT_APP_DISPLAY.copilot_outlook,
  COPILOT_APP_DISPLAY.copilot_one_note,
  COPILOT_APP_DISPLAY.copilot_loop,
];

const LICENSED_USERS_TITLE = ["Licensed Users"];
const LICENSED_USERS_LEGEND = ["Active", "Inactive"];

const PRODUCTS = ["Teams", "Word", "Excel", "PowerPoint", "Outlook", "OneNote", "Loop"] as const;
type Prefix = "ActiveUsers" | "EnabledUsers" | "Utilization";

function getMetricValue(
  data: FormattedLineData<FlattenedCopilotUsageRecord> | undefined,
  val1: number,
  val2: number,
  prefix: Prefix,
): number | undefined {
  const suffix = PRODUCTS[val1];
  if (!suffix) return undefined;

  const key = `${prefix}${suffix}` as const;
  const arr = data?.[key];
  return arr?.[val2];
}

const COPILOT_APP_COLOR_RANGE = [
  COPILOT_APP_COLOR.copilot_msft_teams,
  COPILOT_APP_COLOR.copilot_word,
  COPILOT_APP_COLOR.copilot_excel,
  COPILOT_APP_COLOR.copilot_power_point,
  COPILOT_APP_COLOR.copilot_outlook,
  COPILOT_APP_COLOR.copilot_one_note,
  COPILOT_APP_COLOR.copilot_loop,
];

const LICENSED_USERS_COLOR_RANGE = [reportingColors.blue, reportingColors.yellow];

const ACTIVE_USERS_COLOR_RANGE = [reportingColors.blue];

const bar = "bar";
const line = "line";
export type ChartState = typeof line | typeof bar;

type Props = PropsFromRedux;

export const CopilotReport: FC<Props> = ({ integrations }) => {
  const [activeUsersChartState, setActiveUsersChartState] = useState<ChartState>(bar);
  const [appUsageChartState, setAppUsageChartState] = useState<ChartState>(bar);
  const isMicrosoftIntegrationEnabled = integrations.MSGraph;

  const [filter, setFilter] = useState(defaultDateFilter());

  const handleUsageDropdownChange = useCallback(
    (dropdownValue: string) =>
      dropdownValue === USAGE_BY_APPLICATION_TITLES[0] ? setAppUsageChartState(bar) : setAppUsageChartState(line),
    [],
  );

  const handleActiveUsersDropdownChange = useCallback(
    (dropdownValue: string) =>
      dropdownValue === ACTIVE_USERS_TITLES[0] ? setActiveUsersChartState(bar) : setActiveUsersChartState(line),
    [],
  );

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(dateRange, lineChartMargins.left + lineChartMargins.right);

  const usersTrendQuery = useCopilotUsersTrend({ aggregation: chartPeriod, ...filter });
  const appEngagementTrendQuery = useCopilotAppEngagementTrend({ aggregation: chartPeriod, ...filter });
  const appEngagementTimeseriesQuery = useCopilotAppEngagementTimeseries({ aggregation: chartPeriod, ...filter });
  const userEngagementQuery = useCopilotUserEngagement(filter);

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const activeUsersChart = useMemo(() => {
    return activeUsersChartState === line ? (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={[usersTrendQuery.data?.lineData.date]}
        yData={[usersTrendQuery.data?.lineData.activeUsers]}
        xFormatterFunc={(xValue: string) => getFormattedTimeStringFromPeriod(xValue, chartPeriod, dateRange)}
        colorRange={ACTIVE_USERS_COLOR_RANGE}
        tooltipFormatter={(date, num) => `${getFormattedTimeStringFromPeriod(date, chartPeriod, dateRange)}: ${num}`}
      />
    ) : (
      <GroupedBarChart
        {...sharedGoogleReportingBarProps}
        data={usersTrendQuery.data?.barData ?? []}
        order={usersTrendQuery.data?.order}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toLocaleString(), chartPeriod, dateRange)}
        tooltipFormatter={groupedBarFormatter}
        colorRange={ACTIVE_USERS_COLOR_RANGE}
      />
    );
  }, [usersTrendQuery.data, chartPeriod, dateRange, activeUsersChartState, groupedBarFormatter]);

  const APP_USAGE_Y_DATA = useMemo(
    () => [
      appEngagementTimeseriesQuery.data?.UtilizationTeams,
      appEngagementTimeseriesQuery.data?.UtilizationWord,
      appEngagementTimeseriesQuery.data?.UtilizationExcel,
      appEngagementTimeseriesQuery.data?.UtilizationPowerPoint,
      appEngagementTimeseriesQuery.data?.UtilizationOutlook,
      appEngagementTimeseriesQuery.data?.UtilizationOneNote,
      appEngagementTimeseriesQuery.data?.UtilizationLoop,
    ],
    [appEngagementTimeseriesQuery.data],
  );

  const usageByApplicationChart = useMemo(() => {
    const APP_USAGE_X_DATA = Array(APP_USAGE_Y_DATA.length).fill(appEngagementTimeseriesQuery.data?.Date);

    return appUsageChartState === bar ? (
      <HorizontalBarChart
        {...sharedAccountReportingHorizontalBarProps}
        data={appEngagementTrendQuery.data?.bars ?? []}
        xTickValueType={undefined}
        xAxisTickFormat={(d) => `${Number(d)}%`}
        tooltipFormatter={(dp) => {
          const filteredCopilotData = appEngagementTrendQuery.data?.rawData.find((item) => item.CopilotApp === dp.id);
          return `${filteredCopilotData?.ActiveUsers} | ${Math.round(dp.value)}%`;
        }}
      />
    ) : (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={APP_USAGE_X_DATA}
        yData={APP_USAGE_Y_DATA}
        tooltipFormatter={(_, num, yIndex, xIndex) =>
          `${getMetricValue(appEngagementTimeseriesQuery.data, yIndex, xIndex, "ActiveUsers")} | ${num}%`
        }
        yBlackBoxFormat={(_, num) => `${num}%`}
        xFormatterFunc={(xValue: string) => getFormattedTimeStringFromPeriod(xValue, chartPeriod, dateRange)}
        colorRange={COPILOT_APP_COLOR_RANGE}
      />
    );
  }, [
    appEngagementTrendQuery.data,
    appEngagementTimeseriesQuery.data,
    chartPeriod,
    dateRange,
    appUsageChartState,
    APP_USAGE_Y_DATA,
  ]);

  if (!isMicrosoftIntegrationEnabled) {
    return (
      <div className={sharedStyles.errorSpacing}>
        <ReportUnavailable />
      </div>
    );
  }

  const hasAppUsageBarData =
    appUsageChartState === bar && appEngagementTrendQuery.isSuccess && validBarData(appEngagementTrendQuery.data?.bars);

  const hasAppUsageLineData =
    appUsageChartState === line &&
    appEngagementTimeseriesQuery.isSuccess &&
    validLineData(APP_USAGE_Y_DATA as number[][]);

  const hasLicensedUsersData =
    userEngagementQuery.isSuccess &&
    (userEngagementQuery.data.ActiveUsers > 0 || userEngagementQuery.data.InactiveUsers > 0);

  const hasActiveUsersData = usersTrendQuery.isSuccess && validLineData([usersTrendQuery.data.lineData.activeUsers]);

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <div className={styles.titleRow}>
          <h2>Summary</h2>
          <Tooltip
            showAlways
            target={<FontAwesomeIcon icon={faCircleInfo} className={styles.infoIcon} />}
            position="bottom center"
            content={TOOLTIP_CONTENT}
            tooltipClassName={styles.tooltip}
          />
        </div>
        <ReportingFilter currentFilter={filter} callback={setFilter} />
      </div>
      <h2 className={styles.usage}>Usage</h2>
      <div className={styles.section} ref={measureRef}>
        <ChartWrapper titles={ACTIVE_USERS_TITLES} onChange={handleActiveUsersDropdownChange}>
          <RequestStatusRenderer state={usersTrendQuery.status}>
            {hasActiveUsersData ? activeUsersChart : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.section}>
        <ChartWrapper
          titles={USAGE_BY_APPLICATION_TITLES}
          showLegend={appUsageChartState === line}
          colorRange={COPILOT_APP_COLOR_RANGE}
          legendLabels={USAGE_BY_APPLICATION_LEGEND}
          onChange={handleUsageDropdownChange}
        >
          <RequestStatusRenderer state={appEngagementTrendQuery.status}>
            {hasAppUsageBarData || hasAppUsageLineData ? usageByApplicationChart : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
        <div className={styles.pieContainer}>
          <ChartWrapper
            titles={LICENSED_USERS_TITLE}
            colorRange={LICENSED_USERS_COLOR_RANGE}
            showLegend
            legendLabels={LICENSED_USERS_LEGEND}
          >
            <RequestStatusRenderer state={userEngagementQuery.status}>
              {hasLicensedUsersData ? (
                <PieChart
                  data={{
                    "Active Licensed Users": userEngagementQuery.data.ActiveUsers,
                    "Inactive Licensed Users": userEngagementQuery.data.InactiveUsers,
                  }}
                  innerRadiusPercentage={75}
                  innerText={{
                    title: `${Math.round(userEngagementQuery.data.LicensedPct * 100)}%`,
                    value: "Active Users",
                    reverseStyle: true,
                  }}
                  colorRange={LICENSED_USERS_COLOR_RANGE}
                  tooltipFormatter={(key, value) =>
                    `${key} | ${value} | ${Math.round((value / (userEngagementQuery.data.ActiveUsers + userEngagementQuery.data.InactiveUsers)) * 100)}%`
                  }
                />
              ) : (
                noData(filter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  integrations: selectMenuItemsData(state),
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CopilotReport);
export default ConnectedComponent;
